import React from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PendingIcon from "@mui/icons-material/Pending";
import HistoryIcon from "@mui/icons-material/History";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { toTitleCase } from "../../utility/tools";
import DI from "../../utility/DI";

/**
 * BalanceSummary — net balance card with Settle Up flow and WhatsApp integration.
 */
const BalanceSummary = ({
  balances = [],
  loading = false,
  groupName,
  groupId,
  di: { POST, GET, urls, session, error, success },
  onRefresh,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [settleOpen, setSettleOpen] = React.useState(false);
  const [settleTarget, setSettleTarget] = React.useState(null);
  const [settleAmount, setSettleAmount] = React.useState("");
  const [settleNote, setSettleNote] = React.useState("");
  const [settleLoading, setSettleLoading] = React.useState(false);

  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [settlements, setSettlements] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const openSettle = (b) => {
    setSettleTarget(b);
    setSettleAmount(Math.abs(b.net_amount).toFixed(2));
    setSettleNote("");
    setSettleOpen(true);
  };

  const handleSettle = async () => {
    const amount = parseFloat(settleAmount);
    if (!amount || amount <= 0) return error("Enter a valid amount");
    if (!settleTarget) return;

    setSettleLoading(true);
    try {
      const res = await POST(urls.post.create_settlement, {
        payee_id: settleTarget.other_id,
        amount: amount,
        group_id: groupId || null,
        note: settleNote || null,
      });
      if (res?.success) {
        success(res.message);
        setSettleOpen(false);
        if (onRefresh) onRefresh();
      } else {
        error(res?.message || "Failed to record settlement");
      }
    } catch (e) {
      error("Failed to record settlement");
    }
    setSettleLoading(false);
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = { user_id: session?.id };
      if (groupId) params.group_id = groupId;
      const res = await GET(urls.get.get_settlements, params);
      if (res?.success) setSettlements(res.data || []);
    } catch (e) { }
    setHistoryLoading(false);
  };

  const handleConfirmAction = async (settlementId, action) => {
    try {
      const res = await POST(urls.post.confirm_settlement, {
        settlement_id: settlementId,
        action: action,
      });
      if (res?.success) {
        success(res.message);
        fetchHistory();
        if (onRefresh) onRefresh();
      } else error(res?.message);
    } catch (e) {
      error("Action failed");
    }
  };

  const handleWhatsAppRequest = (b) => {
    if (!b.whatsapp_number) return error("No WhatsApp number found for this user");

    const amount = Math.abs(b.net_amount).toFixed(2);
    const message = `Hey ${toTitleCase(b.other_name)}, just a friendly reminder for the shared expense settlement of ₹${amount} in Spent App. Please settle it when you have a moment!`;
    const url = `https://wa.me/${b.whatsapp_number}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ mx: 2, my: 1.5, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Skeleton width="40%" height={24} />
        <Skeleton width="100%" height={48} sx={{ mt: 1 }} />
        <Skeleton width="80%" height={48} />
      </Paper>
    );
  }

  if (!balances || balances.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          mx: 2, my: 1.5, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider",
          display: "flex", alignItems: "center", gap: 1.5, justifyContent: "space-between",
          background: isDark
            ? "linear-gradient(135deg, rgba(34, 197, 94, 0.12) 0%, rgba(16, 185, 129, 0.08) 100%)"
            : "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 28 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>All settled up! No pending balances.</Typography>
        </Box>
        <IconButton size="small" onClick={() => { fetchHistory(); setHistoryOpen(true); }}><HistoryIcon fontSize="small" /></IconButton>
      </Paper>
    );
  }

  const totalOwed = balances.filter((b) => b.net_amount > 0).reduce((s, b) => s + b.net_amount, 0);
  const totalOwe = balances.filter((b) => b.net_amount < 0).reduce((s, b) => s + Math.abs(b.net_amount), 0);

  return (
    <>
      <Paper elevation={0} sx={{ mx: 2, my: 1.5, borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <Box
          sx={{
            px: 2,
            py: 1.2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: isDark
              ? "linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.85) 100%)"
              : "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: "text.primary" }}>
              {groupName ? `${groupName} — Balances` : "Net Balances"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
            {totalOwed > 0 && (
              <Chip
                size="small"
                label={`+₹${totalOwed.toFixed(0)}`}
                sx={{
                  bgcolor: isDark ? "rgba(34, 197, 94, 0.18)" : "success.light",
                  color: isDark ? "#4ade80" : "success.contrastText",
                  border: isDark ? "1px solid rgba(74, 222, 128, 0.3)" : "none",
                  fontWeight: 700,
                  fontSize: 12,
                  height: 22,
                }}
              />
            )}
            {totalOwe > 0 && (
              <Chip
                size="small"
                label={`-₹${totalOwe.toFixed(0)}`}
                sx={{
                  bgcolor: isDark ? "rgba(239, 68, 68, 0.18)" : "error.light",
                  color: isDark ? "#f87171" : "error.contrastText",
                  border: isDark ? "1px solid rgba(248, 113, 113, 0.3)" : "none",
                  fontWeight: 700,
                  fontSize: 12,
                  height: 22,
                }}
              />
            )}
            <IconButton size="small" onClick={() => { fetchHistory(); setHistoryOpen(true); }}><HistoryIcon fontSize="small" /></IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 1, py: 0.5 }}>
          {balances.map((b) => {
            const isPositive = b.net_amount > 0;
            const hasSettled = b.settled_total > 0;
            return (
              <Box key={b.other_id} sx={{ py: 1, px: 1, borderRadius: 2, "&:hover": { bgcolor: "action.hover" }, transition: "background 0.15s" }}>
                {/* Row 1: Name + Net Amount + Actions */}
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Avatar sx={{ width: 30, height: 30, fontSize: 14, fontWeight: 600, bgcolor: isPositive ? "success.light" : "error.light", color: isPositive ? "success.contrastText" : "error.contrastText" }}>
                      {b.other_name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>{toTitleCase(b.other_name)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {isPositive ? "owes you" : "you owe"}
                        {b.pending_settlements > 0 && <Chip size="small" label={`${b.pending_settlements} pending`} sx={{ ml: 0.5, height: 18, fontSize: 10, bgcolor: "warning.light" }} />}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side: Amount and Settle button in vertical alignment */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.4 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: isPositive ? "success.main" : "error.main", fontSize: 15, lineHeight: 1.2 }}>
                      {isPositive ? "+" : "-"}₹{Math.abs(b.net_amount).toFixed(2)}
                    </Typography>
                    {isPositive && b.whatsapp_number && (
                      <IconButton size="small" color="success" onClick={() => handleWhatsAppRequest(b)} title="Send WhatsApp Reminder" sx={{ p: 0.2 }}>
                        <WhatsAppIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                    {!isPositive && (
                      <Button size="small" variant="outlined" startIcon={<HandshakeIcon sx={{ fontSize: "13px !important" }} />} onClick={() => openSettle(b)} sx={{ textTransform: "none", fontSize: 11, minWidth: 0, px: 1, py: 0.2, height: 24, borderRadius: 1.5 }}>
                        Settle
                      </Button>
                    )}
                  </Box>
                </Box>
                {/* Row 2: Breakdown (only if settlements exist) */}
                {hasSettled && (
                  <Box sx={{ ml: 5.2, mt: 0.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="caption" color="text.secondary">
                      Expenses: <strong>₹{Math.abs(b.expense_total).toFixed(0)}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: "info.main" }}>
                      Settled: <strong>-₹{b.settled_total.toFixed(0)}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: isPositive ? "success.main" : "error.main", fontWeight: 600 }}>
                      Remaining: ₹{Math.abs(b.net_amount).toFixed(0)}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Settle Dialog */}
      <Dialog open={settleOpen} onClose={() => setSettleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><HandshakeIcon color="primary" />Settle Up</Box>
          <IconButton size="small" onClick={() => setSettleOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {settleTarget && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <Typography variant="body2" color="text.secondary">You owe {toTitleCase(settleTarget.other_name)}</Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: "error.main" }}>₹{Math.abs(settleTarget.net_amount).toFixed(2)}</Typography>
            </Box>
          )}
          <TextField 
            label="Settlement Amount" 
            type="number" 
            value={settleAmount} 
            fullWidth 
            size="small" 
            InputProps={{ readOnly: true }}
            helperText="Partial payments are not allowed."
          />
          <TextField label="Note (optional)" value={settleNote} onChange={(e) => setSettleNote(e.target.value)} fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSettle} disabled={settleLoading} startIcon={settleLoading ? <CircularProgress size={16} /> : <HandshakeIcon />}>Record Settlement</Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><HistoryIcon color="primary" />Settlement Ledger</Box>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {historyLoading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress /></Box> : settlements.length === 0 ? <Box sx={{ p: 3, textAlign: "center" }}><Typography color="text.secondary">No settlements yet.</Typography></Box> : (
            <>
              {/* Per-person summary cards */}
              {(() => {
                // Group settlements by the other person
                const personMap = {};
                settlements.forEach(s => {
                  const otherId = s.payer_id == session?.id ? s.payee_id : s.payer_id;
                  const otherName = s.payer_id == session?.id ? s.payee_name : s.payer_name;
                  if (!personMap[otherId]) personMap[otherId] = { name: otherName, settlements: [], confirmedTotal: 0, pendingTotal: 0 };
                  personMap[otherId].settlements.push(s);
                  if (s.status === "confirmed") personMap[otherId].confirmedTotal += s.amount;
                  else personMap[otherId].pendingTotal += s.amount;
                });

                // Match with balance data for expense_total
                return Object.entries(personMap).map(([otherId, data]) => {
                  const balanceInfo = balances?.find(b => String(b.other_id) === String(otherId));
                  const expenseTotal = balanceInfo ? Math.abs(balanceInfo.expense_total) : null;
                  const remaining = balanceInfo ? Math.abs(balanceInfo.net_amount) : null;

                  return (
                    <Box key={otherId} sx={{ mb: 1 }}>
                      {/* Person summary header */}
                      <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider" }}>
                        <Typography variant="subtitle2" fontWeight={700}>{toTitleCase(data.name)}</Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                          {expenseTotal !== null && (
                            <Typography variant="caption" color="text.secondary">
                              Original debt: <strong>₹{expenseTotal.toFixed(0)}</strong>
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "info.main" }}>
                            Settled: <strong>₹{data.confirmedTotal.toFixed(0)}</strong>
                          </Typography>
                          {data.pendingTotal > 0 && (
                            <Typography variant="caption" sx={{ color: "warning.main" }}>
                              Pending: <strong>₹{data.pendingTotal.toFixed(0)}</strong>
                            </Typography>
                          )}
                          {remaining !== null && (
                            <Typography variant="caption" sx={{ color: remaining > 0 ? "error.main" : "success.main", fontWeight: 600 }}>
                              Remaining: ₹{remaining.toFixed(0)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Simple settlement history list */}
                      {(() => {
                        // Show newest first
                        const sorted = [...data.settlements].sort((a, b) => 
                          new Date(b.created_at) - new Date(a.created_at)
                        );
                        return sorted.map((s, idx) => {
                          const iPaid = s.payer_id == session?.id;
                          return (
                            <Box key={s.id}>
                              {idx > 0 && <Divider sx={{ ml: 4 }} />}
                              <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                <Box sx={{ mt: 0.5 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.status === "confirmed" ? "success.main" : "warning.main" }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {iPaid ? "You paid" : `${toTitleCase(s.payer_name)} paid`}
                                      {" "}
                                      <span style={{ color: "#1565c0" }}>₹{s.amount.toFixed(2)}</span>
                                    </Typography>
                                    <Box>
                                      {s.status === "pending" ? (
                                        s.payee_id == session?.id ? (
                                          <Box sx={{ display: "flex", gap: 0.5 }}>
                                            <Button size="small" variant="contained" color="success" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => handleConfirmAction(s.id, "confirm")}>✓</Button>
                                            <Button size="small" variant="outlined" color="error" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => handleConfirmAction(s.id, "reject")}>✕</Button>
                                          </Box>
                                        ) : <Chip size="small" label="Pending Approval" sx={{ height: 20, fontSize: 10 }} color="warning" />
                                      ) : <Chip size="small" label="Confirmed" sx={{ height: 20, fontSize: 10 }} color="success" />}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 1.5, mt: 0.3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
                                    </Typography>
                                    {s.note && <Typography variant="caption" color="text.secondary">• {s.note}</Typography>}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  );
                });
              })()}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setHistoryOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </>
  );
};

export default DI(BalanceSummary);
